import { useApolloClient } from '@apollo/client';
import Decimal from 'decimal.js';
import { findIndex, isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';

import { Button, ButtonProps } from '@/components/form/baseInputs/Button';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useModalState } from '@/hooks/useModalState';

import { useCanEditDispositiveProvisions } from '../authentication/hooks/useCanEditDispositiveProvisions';
import { useEntityDetailsContext } from '../entities/contexts/entityDetails/entityDetails.context';
import { EditEntitySection } from '../entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { useEditEntityTrowser } from '../entities/EditEntitySplitScreen/hooks/useEditEntityTrowser';
import { useDispositiveProvisionsContext } from './contexts/dispositiveProvisions.context';
import { isBeneficiaryDesignationEntityType } from './dispositiveProvisions.label';
import { ActiveDispositionTab } from './DispositiveProvisionsListView/hooks/useDispositiveProvisionsListViewTab';
import { DispositiveProvisionsSplitScreenModal } from './DispositiveProvisionsSplitScreenModal/DispositiveProvisionsSplitScreenModal';

// this is an approximation of the props that can be shared between a normal button and a ButtonWithPopover
type SharedPopoverButtonProps = Omit<
  ButtonProps,
  'variant' | 'size' | 'onMouseLeave'
>;

type SpecifyDispositiveProvisionsButtonProps = {
  buttonProps?: Partial<SharedPopoverButtonProps>;
  behavior?: 'edit' | 'create';
  currentNodeValue?: Decimal;
} & ActiveDispositionTab;

export const SpecifyDispositiveProvisionsButton = ({
  buttonProps,
  behavior = 'create',
  activePrimaryClientIdTab,
  setActivePrimaryClientIdTab,
  currentNodeValue,
}: SpecifyDispositiveProvisionsButtonProps) => {
  const canEdit = useCanEditDispositiveProvisions();

  const {
    primaryClients,
    isTwoClientHousehold,
    firstGrantorDeathId,
    entityType, // This will only be set if the disposing target is an entity
  } = useDispositiveProvisionsContext();

  const isBenDesignation = isBeneficiaryDesignationEntityType(entityType);
  const label = isBenDesignation ? 'beneficiary designations' : 'dispositions';

  // The edit-entity trowser is opened (URL-driven) if the disposing target is
  // an entity; its id comes from the entity-details context.
  const { entityId } = useEntityDetailsContext();
  const { openForEntity } = useEditEntityTrowser();

  // Tracks the DispositiveProvisionsSplitScreenModal state (which is opened if the disposing target is NOT an entity)
  const [
    { isModalOpen: isDPModalOpen },
    { openModal: openDPModal, closeModal: closeDPModal },
  ] = useModalState();

  const handleClick = useCallback(
    (activeClientId?: string) => {
      if (activeClientId) {
        setActivePrimaryClientIdTab(activeClientId);
      }

      const targetClientId = activeClientId || activePrimaryClientIdTab;
      const grantorIndex = findIndex(
        primaryClients,
        (c) => c.id === targetClientId
      );

      // BDG form is a single section that handles both death orders internally,
      // so we ignore grantorIndex when routing to it.
      let section: EditEntitySection;
      if (isBenDesignation) {
        section = EditEntitySection.BENEFICIARY_DESIGNATIONS;
      } else if (grantorIndex === 1) {
        section = EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2;
      } else {
        section = EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1;
      }

      if (entityType && entityId) {
        // Open the edit-entity trowser if the disposing target is an entity
        openForEntity(entityId, { section });
      } else {
        // Open the DispositiveProvisionsSplitScreenModal if the disposing target is not an entity
        openDPModal();
      }
    },
    [
      activePrimaryClientIdTab,
      primaryClients,
      entityType,
      entityId,
      isBenDesignation,
      setActivePrimaryClientIdTab,
      openForEntity,
      openDPModal,
    ]
  );

  const CreateOrEditDispositiveProvisionsButton = useMemo(() => {
    // in the edit scenario, we always expose both possible death orders, via the two tabs
    // so when you hit edit you're already doing it in the context of a specific death order.
    if (behavior === 'edit') {
      return (
        <Button
          startIcon={Edit02Icon}
          size="sm"
          variant="secondary"
          disabled={!canEdit}
          {...buttonProps}
          onClick={() => handleClick()}
        >
          {`Edit ${label}`}
        </Button>
      );
    }

    // BDG form doesn't branch on death order, so skip the per-grantor popover
    // for two-client households.
    if (isTwoClientHousehold && !isBenDesignation) {
      return (
        <ButtonWithPopover
          size="sm"
          variant="primary"
          label={`Specify ${label}`}
          popperVariant="menuBelow"
          disabled={!canEdit}
          {...buttonProps}
        >
          {primaryClients
            .sort((a) => (a.id === firstGrantorDeathId ? -1 : 1))
            .map((client) => (
              <MenuItem
                key={client.id}
                label={`${client.displayName} dies first`}
                onClick={() => handleClick(client.id)}
              />
            ))}
        </ButtonWithPopover>
      );
    }

    return (
      <Button
        size="sm"
        variant="primary"
        onClick={() => handleClick()}
        disabled={!canEdit}
        {...buttonProps}
      >
        {`Specify ${label}`}
      </Button>
    );
  }, [
    behavior,
    isTwoClientHousehold,
    isBenDesignation,
    buttonProps,
    canEdit,
    handleClick,
    primaryClients,
    firstGrantorDeathId,
    label,
  ]);

  const client = useApolloClient();

  const onCloseDPModal = useCallback(() => {
    closeDPModal();

    // since this can be invoked from outside an EW context, we need to refetch
    // the dispositive provisions manually
    void client.refetchQueries({
      include: ['GetDispositiveProvisions'],
    });
  }, [client, closeDPModal]);

  const hasPrimaryClients = !isEmpty(primaryClients);

  if (!canEdit) return null;

  return (
    <>
      {isDPModalOpen && (
        <DispositiveProvisionsSplitScreenModal
          firstClientDeathId={activePrimaryClientIdTab}
          currentNodeValue={currentNodeValue}
          isOpen={
            isDPModalOpen &&
            Boolean(activePrimaryClientIdTab) &&
            hasPrimaryClients
          }
          onClose={onCloseDPModal}
        />
      )}
      {CreateOrEditDispositiveProvisionsButton}
    </>
  );
};
